import { Injectable, inject } from '@angular/core';

import { injectCountryMap } from '@core/country-data';
import { I18nService } from '@core/i18n-legacy';
import { groupByKey, includesWithTrim } from '@core/utils-legacy';

import type { LocationItem, TemplateGroup } from './select-locations-dialog.types';
import { SortKey, SortOption, TemplateKind } from './select-locations-dialog.types';

@Injectable()
export class SelectLocationsDialogGroupsService {
  private readonly i18n = inject(I18nService);

  public sortLabelTranslations = {
    [SortOption.Name]: this.i18n.translate('app.locations.sort_by.name'),
    [SortOption.Country]: this.i18n.translate('app.locations.sort_by.country'),
    [SortOption.State]: this.i18n.translate('app.locations.sort_by.state'),
    [SortOption.City]: this.i18n.translate('app.locations.sort_by.city'),
  };
  private countries = injectCountryMap();
  private groupWithoutKeyTranslations = {
    [SortOption.Name]: '',
    [SortOption.Country]: this.i18n.translate('app.locations.sort_by.without_country'),
    [SortOption.State]: this.i18n.translate('app.locations.sort_by.without_state'),
    [SortOption.City]: this.i18n.translate('app.locations.sort_by.without_city'),
  };

  public sortByLength(templateGroups: TemplateGroup[]): TemplateGroup[] {
    return templateGroups.sort((a, b) => b.locations.length - a.locations.length);
  }

  public filterLocations(locations: LocationItem[], query: string) {
    return query
      ? locations.filter(item => {
          const queryMatchLocationName = includesWithTrim(item.name, query);
          const queryMatchLocationAddress = includesWithTrim(item.fullAddress, query);

          return queryMatchLocationName || queryMatchLocationAddress;
        })
      : [...locations];
  }

  public groupLocations(locations: LocationItem[], sort: SortOption): TemplateGroup[] {
    switch (sort) {
      case SortOption.Name:
        return [
          ...(locations.length
            ? [
                {
                  title: this.sortLabelTranslations[sort],
                  locations,
                  kind: TemplateKind.Default,
                },
              ]
            : []),
        ];
      case SortOption.Country:
      case SortOption.State:
      case SortOption.City:
        return this.convertGroups(locations, sort);
      default:
        return [
          ...(locations.length
            ? [
                {
                  title: this.sortLabelTranslations[sort],
                  locations,
                  kind: TemplateKind.Default,
                },
              ]
            : []),
        ];
    }
  }

  private convertGroups(locations: LocationItem[], sort: SortOption): TemplateGroup[] {
    const groups = groupByKey(locations, <keyof LocationItem>SortKey[sort]);

    return locations.length
      ? Object.keys(groups).map(key => ({
          title: this.getGroupTitle(key, sort),
          locations: groups[key],
          kind: TemplateKind.Group,
          key,
        }))
      : [];
  }

  private getGroupTitle(key: string, sort: SortOption) {
    if (sort === SortOption.Country) {
      const country = this.countries[key]?.name;
      return country ? country : this.groupWithoutKeyTranslations[sort];
    }

    return key !== 'null' ? key : this.groupWithoutKeyTranslations[sort];
  }
}
